import { useEffect, useState } from 'react';
import { fetchSecurityAuditLogs } from '../features/iso27001/security/securityApi';
import {
  SECURITY_AUDIT_ACTION_LABELS,
  type SecurityAuditAction,
  type SecurityAuditLogItem,
} from '../features/iso27001/security/types';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../components/ui/dialog';

const ACTION_OPTIONS: SecurityAuditAction[] = [
  'login_success',
  'login_failed',
  'login_blocked',
  'login_mfa_failed',
  'login_mfa_success',
  'password_changed',
  'password_reset_requested',
  'password_reset_completed',
  'mfa_enrolled',
  'mfa_disabled',
  'permission_role_updated',
  'permission_user_override_updated',
  'export_generated',
];

export function SecurityAuditPage() {
  const [items, setItems] = useState<SecurityAuditLogItem[]>([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [action, setAction] = useState<SecurityAuditAction | ''>('');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [detailLog, setDetailLog] = useState<SecurityAuditLogItem | null>(null);

  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      setLoading(true);
      setError('');
      try {
        const result = await fetchSecurityAuditLogs({
          page,
          pageSize: 50,
          action: action || undefined,
        });
        if (!cancelled) {
          setItems(result.items);
          setTotal(result.total);
        }
      } catch {
        if (!cancelled) {
          setError('No se pudo cargar la bitácora de seguridad');
        }
      } finally {
        if (!cancelled) {
          setLoading(false);
        }
      }
    };
    void load();
    return () => {
      cancelled = true;
    };
  }, [page, action]);

  const totalPages = Math.max(1, Math.ceil(total / 50));

  return (
    <div className='space-y-6 p-6'>
      <div>
        <h1 className='text-2xl font-semibold text-foreground'>Bitácora de seguridad</h1>
        <p className='text-sm text-muted-foreground mt-1'>
          Registro de accesos y eventos sensibles (ISO 27001 A.8.5).
        </p>
      </div>

      <div className='flex flex-wrap gap-4 items-end'>
        <div>
          <label htmlFor='audit-action' className='block text-sm font-medium mb-1'>
            Acción
          </label>
          <select
            id='audit-action'
            value={action}
            onChange={(e) => {
              setPage(1);
              setAction(e.target.value as SecurityAuditAction | '');
            }}
            className='border border-border rounded-md px-3 py-2 bg-background text-foreground min-w-[220px]'
          >
            <option value=''>Todas</option>
            {ACTION_OPTIONS.map((opt) => (
              <option key={opt} value={opt}>
                {SECURITY_AUDIT_ACTION_LABELS[opt]}
              </option>
            ))}
          </select>
        </div>
      </div>

      {error && <p className='text-sm text-red-500'>{error}</p>}

      <div className='overflow-x-auto border border-border rounded-lg'>
        <table className='min-w-full text-sm'>
          <thead className='bg-muted/50'>
            <tr>
              <th className='px-4 py-2 text-left'>Fecha</th>
              <th className='px-4 py-2 text-left'>Acción</th>
              <th className='px-4 py-2 text-left'>Usuario</th>
              <th className='px-4 py-2 text-left'>IP</th>
              <th className='px-4 py-2 text-left'>Detalle</th>
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr>
                <td colSpan={5} className='px-4 py-6 text-center text-muted-foreground'>
                  Cargando...
                </td>
              </tr>
            ) : items.length === 0 ? (
              <tr>
                <td colSpan={5} className='px-4 py-6 text-center text-muted-foreground'>
                  Sin registros
                </td>
              </tr>
            ) : (
              items.map((row) => (
                <tr key={row.id} className='border-t border-border'>
                  <td className='px-4 py-2 whitespace-nowrap'>
                    {new Date(row.at).toLocaleString()}
                  </td>
                  <td className='px-4 py-2'>{SECURITY_AUDIT_ACTION_LABELS[row.action]}</td>
                  <td className='px-4 py-2'>{row.userEmail ?? '—'}</td>
                  <td className='px-4 py-2 font-mono text-xs'>{row.ip}</td>
                  <td className='px-4 py-2 text-xs text-muted-foreground max-w-xs'>
                    {row.metadata ? (
                      <button
                        type="button"
                        className="text-primary underline-offset-4 hover:underline"
                        onClick={() => setDetailLog(row)}
                      >
                        Ver detalle JSON
                      </button>
                    ) : (
                      '—'
                    )}
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      <div className='flex items-center justify-between text-sm'>
        <span className='text-muted-foreground'>
          {total} registro{total === 1 ? '' : 's'}
        </span>
        <div className='flex gap-2'>
          <button
            type='button'
            disabled={page <= 1}
            onClick={() => setPage((p) => Math.max(1, p - 1))}
            className='px-3 py-1 border border-border rounded disabled:opacity-50'
          >
            Anterior
          </button>
          <span>
            {page} / {totalPages}
          </span>
          <button
            type='button'
            disabled={page >= totalPages}
            onClick={() => setPage((p) => p + 1)}
            className='px-3 py-1 border border-border rounded disabled:opacity-50'
          >
            Siguiente
          </button>
        </div>
      </div>

      <Dialog open={Boolean(detailLog)} onOpenChange={(open) => !open && setDetailLog(null)}>
        <DialogContent className="max-w-3xl">
          <DialogHeader>
            <DialogTitle>Detalle de evento de seguridad</DialogTitle>
            <DialogDescription>
              {detailLog
                ? `${SECURITY_AUDIT_ACTION_LABELS[detailLog.action]} · ${new Date(detailLog.at).toLocaleString()}`
                : 'Detalle del registro'}
            </DialogDescription>
          </DialogHeader>
          <pre className="max-h-[60vh] overflow-auto rounded-md border border-border bg-muted p-4 text-xs">
            {detailLog?.metadata ? JSON.stringify(detailLog.metadata, null, 2) : '{}'}
          </pre>
          <DialogFooter>
            <button
              type="button"
              className="btn btn-outline"
              onClick={() => setDetailLog(null)}
            >
              Cerrar
            </button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
